interface AudioCapableWindow {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
}

/** One leg of a tone: a shape, the pitches it sweeps through, and when it sounds relative to the trigger. */
interface ToneLeg {
  type: OscillatorType;
  from: number;
  to: number;
  at: number;
  duration: number;
  peak: number;
}

/** Three outcomes need three sounds: hands on a parcel cannot tell a refusal from a shipment by one chirp. */
export class FulfillmentSound {
  private static shared: AudioContext | null = null;

  /** Rising two-part chime: the shipment left. */
  private static readonly SUCCESS: ToneLeg[] = [
    { type: 'sine', from: 340, to: 940, at: 0, duration: 0.3, peak: 0.22 },
    { type: 'triangle', from: 1250, to: 1250, at: 0.14, duration: 0.22, peak: 0.12 },
  ];

  /** Falling square buzz, twice: the write was refused and nothing moved. */
  private static readonly FAILURE: ToneLeg[] = [
    { type: 'square', from: 320, to: 150, at: 0, duration: 0.18, peak: 0.16 },
    { type: 'square', from: 320, to: 150, at: 0.22, duration: 0.18, peak: 0.16 },
  ];

  /** One flat sawtooth note held: the write landed but something on it needs answering. */
  private static readonly WARNING: ToneLeg[] = [
    { type: 'sawtooth', from: 620, to: 620, at: 0, duration: 0.34, peak: 0.14 },
  ];

  /** The write went through. */
  public static success(): void {
    FulfillmentSound.play(FulfillmentSound.SUCCESS);
  }

  /** The write was refused; nothing changed. */
  public static failure(): void {
    FulfillmentSound.play(FulfillmentSound.FAILURE);
  }

  /** The write landed with something the operator still has to answer for. */
  public static warning(): void {
    FulfillmentSound.play(FulfillmentSound.WARNING);
  }

  /** Drops the cached context so a test — or a browser that lost its audio device — starts clean. */
  public static reset(): void {
    FulfillmentSound.shared = null;
  }

  private static context(): AudioContext | null {
    const owner = window as unknown as AudioCapableWindow;
    const Ctor = owner.AudioContext ?? owner.webkitAudioContext;
    if (!Ctor) {
      return null;
    }
    FulfillmentSound.shared = FulfillmentSound.shared ?? new Ctor();
    return FulfillmentSound.shared;
  }

  private static play(legs: ToneLeg[]): void {
    try {
      const audio = FulfillmentSound.context();
      if (!audio) {
        return;
      }
      // Autoplay policy suspends the context until a gesture reaches it — the click is that gesture.
      if (audio.state === 'suspended') {
        void audio.resume();
      }
      const start = audio.currentTime;
      for (const leg of legs) {
        FulfillmentSound.sound(audio, leg, start);
      }
    } catch {
      // A silent desk is not a reason to fail the write.
    }
  }

  private static sound(audio: AudioContext, leg: ToneLeg, start: number): void {
    const at = start + leg.at;
    const end = at + leg.duration;
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();
    oscillator.type = leg.type;
    oscillator.frequency.setValueAtTime(leg.from, at);
    if (leg.to !== leg.from) {
      oscillator.frequency.exponentialRampToValueAtTime(leg.to, end - 0.02);
    }
    // Ramps from a hair above zero because an exponential ramp cannot touch it.
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(leg.peak, at + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    oscillator.connect(gain);
    gain.connect(audio.destination);
    oscillator.start(at);
    oscillator.stop(end + 0.02);
  }
}
